import { FC, useMemo } from 'react';
import { ArrowBackIos, ArrowForwardIos, Lock } from '@mui/icons-material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { FEATURE_FLAGS, FUZE_CASH, PAYMENT_STEP } from '~common';
import { InsufficientBalanceAlert } from '~components/atoms/new-step-three/InsufficientBalanceAlert';
import { ListItem } from '~components/atoms/new-step-three/ListItem';
import {
  MethodItem,
  PaymentMethodItem,
  WalletPaymentMethodItem,
} from '~components/atoms/new-step-three/PaymentMethodItem';
import { PreAuthText } from '~components/atoms/new-step-three/PreAuthText';
import { PricingText } from '~components/atoms/new-step-three/PricingText';
import { RentChargerButton } from '~components/atoms/new-step-three/RentChargerButton';
import { StripeElements } from '~components/molecules/StripeElements';
import { LocalizationTypography } from '~components/organisms/localization';
import {
  getWalletSetupElementsOptions,
  stripeElementsOptions,
} from '~configs/stripe';
import { useQueryGetCampusCardBalance } from '~hooks';
import {
  isSelectedCampusCard,
  useAccountBalancesContext,
  usePaymentStepContext,
  useStationInformationContext,
  useStationPricingContext,
  useUserPaymentContext,
} from '~providers';
import { checkEnabledFeatureFlag, formatCurrency } from '~utils';
import { addToCurrentDate } from '~utils/date.util';

export const RentChargerStep: FC = () => {
  const { stationPricing, hasMultiplePricingOptions } =
    useStationPricingContext();
  const { handleSetPaymentStep, selectedPaymentMethod, selectedPricing } =
    usePaymentStepContext();
  const { campusCard } = useStationInformationContext();
  const { userCampusCards } = useUserPaymentContext();

  const hasLinkedSupportedCard =
    campusCard.enabled &&
    userCampusCards.some(
      (card) =>
        !campusCard.institutionId ||
        card.institutionUuid === campusCard.institutionId,
    );
  const shouldShowStudentAccountCta =
    campusCard.enabled &&
    !isSelectedCampusCard(selectedPaymentMethod) &&
    !hasLinkedSupportedCard;

  const { accountBalancesData } = useAccountBalancesContext();

  const selectedCampusCardId = isSelectedCampusCard(selectedPaymentMethod)
    ? selectedPaymentMethod.card.id
    : undefined;
  const campusCardSchoolName = isSelectedCampusCard(selectedPaymentMethod)
    ? selectedPaymentMethod.card.institutionName
    : '';
  const { data: campusCardBalance, isFetching: isFetchingCampusCardBalance } =
    useQueryGetCampusCardBalance(selectedCampusCardId);

  const pricingModel = selectedPricing || stationPricing;

  // The new wallet flow (ExpressCheckoutElement + confirmSetup) needs Elements
  // mounted in `setup` mode with a currency; the legacy PaymentRequestButton
  // path only needs the appearance-only options.
  const isMultiCardsPerUserEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.MULTI_CARDS_PER_USER_BY_WEB_PLATFORM,
  );
  const elementsOptions = useMemo(
    () =>
      isMultiCardsPerUserEnabled
        ? getWalletSetupElementsOptions(pricingModel?.currency ?? 'usd')
        : stripeElementsOptions,
    [isMultiCardsPerUserEnabled, pricingModel?.currency],
  );

  const isSubscriptionPricingModel = pricingModel?.allowBatterySwap === true;

  // Campus card balance must cover the max charge amount; normal rentals
  // (non-subscription) also need to cover the unreturned fee.
  let requiredCampusCardBalance: number | null = null;
  if (pricingModel != null) {
    requiredCampusCardBalance = isSubscriptionPricingModel
      ? pricingModel.maxChargeAmount
      : pricingModel.maxChargeAmount + (pricingModel.unreturnedFee ?? 0);
  }

  const isInsufficientCampusCardBalance =
    campusCardBalance != null &&
    requiredCampusCardBalance != null &&
    campusCardBalance.balance < requiredCampusCardBalance;

  // A day-unit pass is a fixed-duration pass, not a subscription, so it needs a
  // return-by date too: at day scale that date is what drives the unreturned
  // fee. Hourly and minute passes keep their existing behaviour of showing none.
  const isDayScalePricingModel =
    pricingModel?.maxRentTimeUnit?.toLowerCase() === 'days';

  const expectedEndAt =
    (isSubscriptionPricingModel || isDayScalePricingModel) &&
    pricingModel?.maxRentTime &&
    pricingModel?.maxRentTimeUnit
      ? addToCurrentDate(
          pricingModel.maxRentTime,
          pricingModel.maxRentTimeUnit,
        ).toISOString()
      : undefined;

  const matchingBalance = accountBalancesData?.accountBalances.find(
    (balance) => balance.currency === pricingModel?.currency,
  );

  const hasAvailableBalance = accountBalancesData?.accountBalances.some(
    (balance) =>
      balance.symbol === pricingModel?.symbol && balance.availableBalance > 0,
  );

  const onClickAddPromoBtn = () => {
    handleSetPaymentStep(PAYMENT_STEP.ADD_PROMO_CODE);
  };

  if (!selectedPaymentMethod) {
    return null;
  }

  return (
    <>
      <Stack display="grid" gap={1}>
        <Stack direction="row">
          {hasMultiplePricingOptions && (
            <IconButton
              aria-label="back"
              size="small"
              color="inherit"
              sx={{ paddingY: 0 }}
              onClick={() =>
                handleSetPaymentStep(PAYMENT_STEP.SELECT_PRICING_MODELS)
              }
            >
              <ArrowBackIos />
            </IconButton>
          )}
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.rentCharger.title"
            defaultText="Rent a Charger"
            variant="Poppins/Heading-3"
          />
        </Stack>

        {pricingModel && (
          <PricingText
            price={pricingModel.price}
            unit={pricingModel.unit}
            unitQuantity={pricingModel.unitQuantity}
            currencySymbol={pricingModel.symbol || ''}
            allowBatterySwap={pricingModel.allowBatterySwap}
            maxSwaps={pricingModel.maxSwap}
            endAt={expectedEndAt}
            maxChargeAmount={pricingModel.maxChargeAmount}
          />
        )}
      </Stack>

      {/* DIVIDER */}
      <Box
        sx={{
          marginX: '-20px',
          height: '2px',
          backgroundColor: '#F3F4F6',
        }}
      />

      {/* PAY WITH STUDENT ACCOUNT CTA */}
      {shouldShowStudentAccountCta && (
        <ButtonBase
          onClick={() => handleSetPaymentStep(PAYMENT_STEP.CAMPUS_CARD_LIST)}
          sx={{
            width: '100%',
            bgcolor: 'brand.lightest',
            border: '1px solid #CCEDFF',
            borderRadius: '24px',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <SchoolOutlinedIcon
            sx={{ width: 24, height: 24, color: 'brand.darkest' }}
          />
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.rentCharger.payWithStudent"
            defaultText="Pay with your student account"
            variant="Poppins/Subheading-md"
            sx={{ color: 'brand.darkest', flex: 1, textAlign: 'left' }}
          />
          <ArrowForwardIos sx={{ color: 'brand.main', fontSize: '14px' }} />
        </ButtonBase>
      )}

      {/* PAYMENT METHOD */}
      <Stack>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.rentCharger.payment"
          defaultText="Payment"
          variant="Poppins/Heading-6"
          color="#111928"
        />
        <MethodItem
          imgSrc="/images/card-icons/promo-code.svg"
          imgAlt="promo-code"
          title={
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.selectPaymentMethod.promoTitle"
              defaultText="Add Promo Code"
              variant="Poppins/Subheading-md"
            />
          }
          onClick={onClickAddPromoBtn}
          action={
            <IconButton onClick={onClickAddPromoBtn}>
              <ArrowForwardIos sx={{ color: 'brand.main', fontSize: '14px' }} />
            </IconButton>
          }
        />
        {hasAvailableBalance && (
          <ListItem
            left={<img src="/images/fuze_cash.svg" alt="fuze-cash" />}
            right={
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  width: '100%',
                }}
              >
                <Typography variant="Poppins/Subheading-md" color="#111928">
                  FUZE Cash
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {matchingBalance && (
                    <Typography variant="Poppins/Subheading-md" color="#111928">
                      {matchingBalance.availableBalance >=
                      FUZE_CASH.FUZE_CASH_MAX_AMOUNT
                        ? `${matchingBalance.symbol}${FUZE_CASH.FUZE_CASH_MAX_AMOUNT_DISPLAY}`
                        : formatCurrency(
                            matchingBalance.availableBalance,
                            matchingBalance.symbol,
                            2,
                          )}
                    </Typography>
                  )}
                  <IconButton
                    onClick={() =>
                      handleSetPaymentStep(PAYMENT_STEP.FUZE_CASH_BALANCES)
                    }
                  >
                    <ArrowForwardIos
                      sx={{ color: 'brand.main', fontSize: '14px' }}
                    />
                  </IconButton>
                </Stack>
              </Stack>
            }
            onContainerClick={() =>
              handleSetPaymentStep(PAYMENT_STEP.FUZE_CASH_BALANCES)
            }
          />
        )}
        {selectedPaymentMethod === 'WALLET' ? (
          <WalletPaymentMethodItem
            action={
              <IconButton>
                <ArrowForwardIos
                  sx={{ color: 'brand.main', fontSize: '14px' }}
                />
              </IconButton>
            }
            onClick={() =>
              handleSetPaymentStep(PAYMENT_STEP.SELECT_PAYMENT_METHOD)
            }
          />
        ) : isSelectedCampusCard(selectedPaymentMethod) ? (
          <MethodItem
            icon={
              <SchoolOutlinedIcon
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: 30,
                }}
              />
            }
            title={
              <Stack direction="column">
                <LocalizationTypography
                  textId="app.rentalPage.newStepThree.selectPaymentMethod.campusCard"
                  defaultText="Campus Card"
                  variant="Poppins/Subheading-md"
                />
                {campusCardSchoolName && (
                  <Typography variant="Inter/Body-sm" color="text.secondary">
                    {campusCardSchoolName}
                  </Typography>
                )}
              </Stack>
            }
            enableDivider={false}
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                {isFetchingCampusCardBalance ? (
                  <CircularProgress size={16} sx={{ color: 'brand.main' }} />
                ) : campusCardBalance ? (
                  <Typography variant="Poppins/Subheading-md" color="#111928">
                    {formatCurrency(
                      campusCardBalance.balance,
                      pricingModel?.symbol ?? '',
                      2,
                    )}
                  </Typography>
                ) : null}
                <IconButton>
                  <ArrowForwardIos
                    sx={{ color: 'brand.main', fontSize: '14px' }}
                  />
                </IconButton>
              </Stack>
            }
            onClick={() =>
              handleSetPaymentStep(PAYMENT_STEP.SELECT_PAYMENT_METHOD)
            }
          />
        ) : (
          <PaymentMethodItem
            card={selectedPaymentMethod}
            enableDivider={false}
            action={
              <IconButton>
                <ArrowForwardIos
                  sx={{ color: 'brand.main', fontSize: '14px' }}
                />
              </IconButton>
            }
            onClick={() =>
              handleSetPaymentStep(PAYMENT_STEP.SELECT_PAYMENT_METHOD)
            }
          />
        )}

        {/* Insufficient Campus Card Balance Banner */}
        {isInsufficientCampusCardBalance && <InsufficientBalanceAlert />}
      </Stack>

      <StripeElements options={elementsOptions}>
        {/* PAYMENT BUTTON */}
        <Stack gap={2}>
          <Stack>
            {/* PRE-AUTH TEXT */}
            <PreAuthText />

            {/* UNRETURNED FEE TEXT */}
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.rentCharger.unreturnedFeeNotice"
              defaultText="A {{unreturnedFee}} fee applies if the charger isn’t returned."
              textValues={{
                unreturnedFee: formatCurrency(
                  pricingModel?.unreturnedFee,
                  pricingModel?.symbol,
                ),
                interpolation: {
                  escapeValue: false, // allow interpolation ("/" in this case) to be used
                },
              }}
              sx={{
                fontSize: '12px',
                color: '#6B7280',
                fontFamily: 'Poppins',
                lineHeight: '16px',
                fontWeight: 400,
                textAlign: 'center',
              }}
            />
          </Stack>
          <RentChargerButton
            disabled={
              isInsufficientCampusCardBalance || isFetchingCampusCardBalance
            }
          />
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
            sx={{ color: '#6B7280' }}
          >
            <Lock sx={{ fontSize: 16 }} />
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.rentCharger.secured"
              defaultText="Secure and encrypted"
              sx={{
                fontSize: '12px',
                fontFamily: 'Poppins',
                lineHeight: '16px',
                fontWeight: 400,
                textAlign: 'center',
              }}
            />
          </Stack>
        </Stack>
      </StripeElements>
    </>
  );
};
